import { getAuthentication } from "../../lib/auth";
import { ARTICLE_URL } from "../../lib/env";
import { escapeGqlString, gqlRequest } from "../../lib/functions";
import { ARTICLEGQLSCHEMA } from "../../lib/gql/Article";
import { IArticle } from "../../types/Article";
import { IMultiLanguageInputArgs } from "../../types/MultiLanguageInput";

export async function getArticles(
  limit: number,
  offset: number,
  query: string
): Promise<IArticle[]> {
  let filters = "";
  if (query && query.trim() !== "") {
    filters = `,query:"${query}"`;
  }
  const gqlString = `query{
    GetArticles(limit:${limit},offset:${offset}${filters}){
        ${ARTICLEGQLSCHEMA}
    }
  }`;
  try {
    const response = await gqlRequest<{
      GetArticles: IArticle[];
    }>({
      query: gqlString,
      endpoint: ARTICLE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.GetArticles || [];
  } catch (err) {
    throw err;
  }
}

export async function getArticleByID(id: string): Promise<IArticle | null> {
  const gqlString = `query{
        GetArticleByID(id:"${id}"){
            ${ARTICLEGQLSCHEMA}
        }
    }`;

  try {
    const response = await gqlRequest<{
      GetArticleByID: IArticle;
    }>({
      query: gqlString,
      endpoint: ARTICLE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.GetArticleByID || null;
  } catch (err) {
    throw err;
  }
}

export async function addNewArticle(
  title: IMultiLanguageInputArgs
): Promise<IArticle | null> {
  const gqlString = `mutation{
        AddNewArticle(title:{
                    en:"${escapeGqlString(title.en)}",
                    ar:"${escapeGqlString(title.ar)}",
                    fr:"${escapeGqlString(title.fr)}"
        }){
            ${ARTICLEGQLSCHEMA}            
        }
    }`;

  try {
    const response = await gqlRequest<{
      AddNewArticle: IArticle;
    }>({
      query: gqlString,
      endpoint: ARTICLE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.AddNewArticle || null;
  } catch (err) {
    throw err;
  }
}

export async function editArticleTitle(
  id: string,
  title: IMultiLanguageInputArgs
): Promise<IArticle | null> {
  const gqlString = `mutation{
        EditArticleTitle(id:"${id}",title:{
                    en:"${escapeGqlString(title.en)}",
                    ar:"${escapeGqlString(title.ar)}",
                    fr:"${escapeGqlString(title.fr)}"
        }){
            ${ARTICLEGQLSCHEMA}            
        }
    }`;
  try {
    const response = await gqlRequest<{
      EditArticleTitle: IArticle;
    }>({
      query: gqlString,
      endpoint: ARTICLE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.EditArticleTitle || null;
  } catch (err) {
    throw err;
  }
}

export async function editArticleDescription(
  id: string,
  description: IMultiLanguageInputArgs
): Promise<IArticle | null> {
  const gqlString = `mutation{
        EditArticleDescription(id:"${id}",description:{
                    en:"${escapeGqlString(description.en)}",
                    ar:"${escapeGqlString(description.ar)}",
                    fr:"${escapeGqlString(description.fr)}"
        }){
            ${ARTICLEGQLSCHEMA}            
        }
    }`;
  try {
    const response = await gqlRequest<{
      EditArticleDescription: IArticle;
    }>({
      query: gqlString,
      endpoint: ARTICLE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.EditArticleDescription || null;
  } catch (err) {
    throw err;
  }
}

export async function updateArticleImage(
  id: string,
  image: string
): Promise<IArticle | null> {
  const gqlString = `mutation{
        UpdateArticleImage(id:"${id}",image:"${image}"){
            ${ARTICLEGQLSCHEMA}            
        }
    }`;
  try {
    const response = await gqlRequest<{
      UpdateArticleImage: IArticle;
    }>({
      query: gqlString,
      endpoint: ARTICLE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.UpdateArticleImage || null;
  } catch (err) {
    throw err;
  }
}

export async function editArticleContent(
  id: string,
  content: IMultiLanguageInputArgs
): Promise<IArticle | null> {
  const gqlString = `mutation{
        EditArticleContent(id:"${id}",content:{
                en:"${escapeGqlString(content.en)}",
                ar:"${escapeGqlString(content.ar)}",
                fr:"${escapeGqlString(content.fr)}"
        }){
            ${ARTICLEGQLSCHEMA}            
        }
    }`;
  try {
    const response = await gqlRequest<{
      EditArticleContent: IArticle;
    }>({
      query: gqlString,
      endpoint: ARTICLE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.EditArticleContent || null;
  } catch (err) {
    throw err;
  }
}

export async function pushKeywordIntoArticle(
  id: string,
  content: IMultiLanguageInputArgs
): Promise<IArticle | null> {
  const gqlString = `mutation{
          PushKeywordIntoArticle(id:"${id}",keyword:{
                  en:"${escapeGqlString(content.en)}",
                  ar:"${escapeGqlString(content.ar)}",
                  fr:"${escapeGqlString(content.fr)}"
          }){
              ${ARTICLEGQLSCHEMA}            
          }
      }`;
  try {
    const response = await gqlRequest<{
      PushKeywordIntoArticle: IArticle;
    }>({
      query: gqlString,
      endpoint: ARTICLE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.PushKeywordIntoArticle || null;
  } catch (err) {
    throw err;
  }
}

export async function pullKeywordFromArticle(
  id: string,
  keyword: IMultiLanguageInputArgs
): Promise<IArticle | null> {
  const gqlString = `mutation{
            PullKeywordFromArticle(id:"${id}",keyword:{
                    en:"${escapeGqlString(keyword.en)}",
                    ar:"${escapeGqlString(keyword.ar)}",
                    fr:"${escapeGqlString(keyword.fr)}"
            }){
                ${ARTICLEGQLSCHEMA}            
            }
        }`;
  try {
    const response = await gqlRequest<{
      PullKeywordFromArticle: IArticle;
    }>({
      query: gqlString,
      endpoint: ARTICLE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.PullKeywordFromArticle || null;
  } catch (err) {
    throw err;
  }
}

export async function deleteArticle(id: string): Promise<boolean | undefined> {
  const gqlString = `mutation{
        DeleteArticle(id:"${id}")
    }`;
  try {
    const response = await gqlRequest<{
      DeleteArticle: boolean;
    }>({
      query: gqlString,
      endpoint: ARTICLE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.DeleteArticle;
  } catch (err) {
    throw err;
  }
}
